import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../images/icons/hub_icon.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { getDeviceList } from '../../../../store/devices/actionCreators';
import { ReactComponent as HubAssignFailedIcon } from '../../../../images/icons/hub_assign_failed.svg';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import CustomerCareSelect from '../../../Shared/CustomerCareSelect';
import { updateDeviceAssociationWithFacility } from '../../../../store/facility/actionCreators';
import { FacilityDeviceAssociationAction } from '../../../../store/facility/facilityTypes';
import { borderGrey, lightBlack, lightGrey } from '../../../../mainTheme';

const STEPS = {
  SELECT_HUB: 'select_hub',
  STATUS: 'status',
};

const AddHubToFacilityModal = ({
  onClose,
  locationId,
  locations,
}: {
  onClose: (success?: boolean) => void;
  locationId?: number;
  locations?: Array<{ id: number; name: string }>;
}) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_HUB);
  const [selectedHub, setSelectedHub] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(
    locationId || null,
  );
  const [status, setStatus] = useState<'success' | 'error' | null>(null);

  const { deviceList, isLoading: isDeviceLoading } = useSelector(
    (state: RootState) => state.devices,
  );

  useEffect(() => {
    dispatch(getDeviceList(false, false));
  }, [dispatch]);

  const availableHubs = React.useMemo(() => {
    return (deviceList ?? [])
      .filter((device) => !device.facility_id && !device.customer_id)
      .map((device) => ({
        id: device.id,
        name: device.serial_number,
      }));
  }, [deviceList]);

  const handleAdd = async () => {
    if (selectedHub && (locationId || selectedLocation)) {
      const deviceIds = [selectedHub.id];
      const targetLocationId = locationId || selectedLocation!;

      setCurrentStep(STEPS.STATUS);

      try {
        await dispatch(
          updateDeviceAssociationWithFacility(
            targetLocationId,
            FacilityDeviceAssociationAction.LINK,
            deviceIds,
            true, // Indicate this is from modern facility UI
          ),
        );
        setStatus('success');
      } catch (error) {
        setStatus('error');
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SELECT_HUB:
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <HubIcon />
            <Typography>Assign a Hub</Typography>
            {isDeviceLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Autocomplete
                  options={availableHubs}
                  getOptionLabel={(option) => option.name}
                  onChange={(_event, newValue) => setSelectedHub(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Hub serial number"
                      placeholder="Hub serial number"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          color: lightBlack,
                          fontSize: '16px',
                        },
                        '& .MuiInputLabel-root': {
                          color: `${lightGrey} !important`,
                          fontSize: '16px',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '& fieldset': {
                            borderColor: borderGrey,
                            borderRadius: '8px',
                          },
                          '&:hover fieldset': {
                            borderColor: borderGrey,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: borderGrey,
                          },
                        },
                        '.MuiAutocomplete-endAdornment': {
                          top: '50%',
                          transform: 'translateY(-50%)',
                        },
                        '.MuiAutocomplete-clearIndicator': {
                          '& svg': {
                            width: 20,
                            height: 20,
                          },
                        },
                      }}
                    />
                  )}
                  popupIcon={
                    <FontAwesomeIcon
                      icon={faAngleDown}
                      style={{
                        width: '20px',
                        height: '20px',
                      }}
                      color="#9999A3"
                    />
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={option.id}
                      style={{
                        color: lightBlack,
                        padding: '8px 16px',
                        fontSize: '16px',
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{option.name}</span>
                    </li>
                  )}
                  ListboxProps={{
                    style: {
                      border: `1px solid ${borderGrey}`,
                      borderRadius: '8px',
                      marginTop: '14px',
                      boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.4)', // match CustomerCareSelect
                    },
                  }}
                />

                {!locationId && (
                  <CustomerCareSelect
                    label="Location"
                    value={selectedLocation || ''}
                    onChange={(e) =>
                      setSelectedLocation(Number(e.target.value))
                    }
                    fullWidth
                  >
                    {(locations ?? []).map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </CustomerCareSelect>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => onClose()}
                    sx={{
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: '18px',
                      letterSpacing: '0.2px',
                      height: '44px',
                      width: '180px',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleAdd}
                    disabled={
                      !selectedHub || (!locationId && !selectedLocation)
                    }
                    sx={{
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: '18px',
                      letterSpacing: '0.2px',
                      height: '44px',
                      width: '180px',
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );
      case STEPS.STATUS:
        if (status === 'success') {
          return (
            <StatusMessage
              icon={<UpdateSwSuccessIcon />}
              title="Added successfully!"
              onClose={() => onClose(true)}
            />
          );
        } else if (status === 'error') {
          return (
            <StatusMessage
              icon={<HubAssignFailedIcon />}
              title={'Failed to assign Hub'}
              message={'Please try again or come back later.'}
              onClose={() => setCurrentStep(STEPS.SELECT_HUB)}
              primaryButtonText="Try again"
            />
          );
        } else {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          );
        }
      default:
        return null;
    }
  };

  return renderStep();
};

export default AddHubToFacilityModal;
